import { login } from "api/authentication";
import { redirectLogin } from "api/authentication/helper";
import { LoginResponse } from "api/authentication/interfaces";
import {
  PASSWORD_REQUIREMENTS_NOT_MET,
  TOO_MANY_UNSUCCESSFUL_ATTEMPTS,
  WRONG_PASSWORD,
} from "api/errors";
import { hasErrorCode } from "api/helper";
import { TextField } from "components/inputs";
import Link from "components/Link";
import { Formik, Form as FormikForm } from "formik";
import useGoogleRecaptcha from "hooks/useGoogleRecaptcha";
import useLocalizedRoutes from "hooks/useLocalizedRoutes";
import {
  errorToast,
  Text,
  usePublicPagePopupsContext,
} from "iparque-components";
import { AppContext } from "providers/AppProvider";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { formatDate, parseServerDate } from "utils/date";
import { gaCategory, gaEvents } from "utils/googleAnalytics";
import { twoFactorAuthenticationStates } from "utils/twoFactorAuthentication";
import FormButton from "../FormButton";
import { getValidationSchema, initialValues } from "./configs";
import { LoginFormValues } from "./interfaces";
import PasswordRequirementsErrorPopup from "./PasswordRequirementsErrorPopup";
import PasswordRequirementsWarningPopup from "./PasswordRequirementsWarningPopup";

const LoginForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { setIsLoading } = useContext(AppContext);
  const navigate = useNavigate();
  const { executeGoogleRecaptcha, isGoogleRecaptchaLoaded } =
    useGoogleRecaptcha();
  const loginResponse = useRef<LoginResponse>();
  const [
    shouldDisplayPasswordRequirementsWarning,
    setShouldDisplayPasswordRequirementsWarning,
  ] = useState(false);
  const [
    shouldDisplayPasswordRequirementsError,
    setShouldDisplayPasswordRequirementsError,
  ] = useState(false);
  const { dismissPopups, displayPopups } = usePublicPagePopupsContext();
  const { getLanguageRoute } = useLocalizedRoutes();

  const continueLogin = useCallback(
    async ({ changePassword = false } = {}) => {
      if (!loginResponse.current) {
        return;
      }

      const { entities, authToken, twoFactorAuthenticationStateId } =
        loginResponse.current;

      if (changePassword) {
        if (entities.length > 1) {
          navigate(getLanguageRoute("passwordRecovery"));
        }

        return;
      }

      if (
        twoFactorAuthenticationStateId !==
        twoFactorAuthenticationStates.inactive
      ) {
        navigate(getLanguageRoute("twoFactorAuthentication"), {
          state: { entities, authToken },
        });

        return;
      }

      if (changePassword) {
        if (entities.length === 1) {
          setIsLoading(true);

          await redirectLogin(
            entities[0],
            authToken,
            { t, stopLoading: () => setIsLoading(false) },
            {
              mod: "utilizador",
              action: "updateUtilizador",
              changePassword: true,
            }
          );
        }

        return;
      }

      if (entities.length === 1) {
        setIsLoading(true);

        await redirectLogin(entities[0], authToken, {
          t,
          stopLoading: () => setIsLoading(false),
        });
      } else {
        navigate(getLanguageRoute("entities"), {
          state: { entities, authToken },
        });
      }
    },
    [getLanguageRoute, navigate, setIsLoading, t]
  );

  useEffect(() => {
    return displayPopups;
  }, [displayPopups]);

  const onSubmitHandler = async (values: LoginFormValues) => {
    setIsLoading(true);
    dismissPopups();

    const recaptchaToken = await executeGoogleRecaptcha();
    const { data, error } = await login(
      values.email,
      values.password,
      recaptchaToken
    );

    setIsLoading(false);

    if (data) {
      ReactGA.event({
        category: gaCategory,
        action: gaEvents.loginSuccess,
      });

      loginResponse.current = {
        ...data,
        authToken: data.token,
        entities: data.entities.map((entity) => ({
          ...entity,
          entityToken: entity.token,
        })),
      };

      const isPasswordValid = data.passwordMeetsRequirements;

      if (!isPasswordValid) {
        setShouldDisplayPasswordRequirementsWarning(true);

        return;
      }

      continueLogin();

      return;
    }

    ReactGA.event({
      category: gaCategory,
      action: gaEvents.loginError,
    });

    if (hasErrorCode(PASSWORD_REQUIREMENTS_NOT_MET, error)) {
      setShouldDisplayPasswordRequirementsError(true);

      return;
    }

    if (hasErrorCode(WRONG_PASSWORD, error)) {
      errorToast(
        t("7847") /* Erro! */,
        t("10859") /* Os dados de autenticação introduzidos estão incorretos. */
      );

      return;
    }

    if (hasErrorCode(TOO_MANY_UNSUCCESSFUL_ATTEMPTS, error)) {
      errorToast(
        t("7847") /* Erro! */,
        t(
          "10860"
        ) /* A sua conta encontra-se, de momento, bloqueada devido a várias tentativas falhadas de autenticação. Para mais informações, por favor, contacte os nossos serviços de apoio. */
      );
      return;
    }

    errorToast(
      t("7847") /* Erro! */,
      t("7859") /* Ocorreu um erro ao realizar o Login. */
    );
  };

  const deadline = loginResponse.current?.passwordRequirementsDeadline
    ? formatDate(
        parseServerDate(loginResponse.current.passwordRequirementsDeadline),
        { dateFormat: "dateTimeWithoutSeconds", locale: i18n.language }
      )
    : "";

  return (
    <>
      <PasswordRequirementsWarningPopup
        visible={shouldDisplayPasswordRequirementsWarning}
        deadline={deadline}
        onClose={() => setShouldDisplayPasswordRequirementsWarning(false)}
        onContinue={continueLogin}
        onClickUrl={() => continueLogin({ changePassword: true })}
      />
      <PasswordRequirementsErrorPopup
        visible={shouldDisplayPasswordRequirementsError}
        onClose={() => setShouldDisplayPasswordRequirementsError(false)}
      />
      <Formik
        initialValues={initialValues}
        validationSchema={getValidationSchema(t)}
        onSubmit={onSubmitHandler}
      >
        <FormikForm>
          <TextField
            type="email"
            id="email-field"
            name="email"
            label={t("178") /* Email */}
          />
          <TextField
            type="password"
            id="password-field"
            name="password"
            label={t("3550") /* Senha */}
          />
          <Text color="quaternary" variant="body6">
            <Link
              to={getLanguageRoute("passwordRecovery")}
              aria-label={t("7865") /* Recuperar password */}
            >
              {t("10967") /* Não se lembra da senha? */}
            </Link>
          </Text>
          <FormButton
            type="submit"
            disabled={!isGoogleRecaptchaLoaded}
            className="mb-15"
          >
            {t("61") /* Entrar */}
          </FormButton>
        </FormikForm>
      </Formik>
    </>
  );
};

export default LoginForm;
